import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AOCScoringCategoryModel,
  EventDivisionBaseModel,
  EventJudgeModel,
  PageantAgeGroupBaseModel,
  PageantModel,
  ReceivedScoreBaseModel,
} from 'API/Generated/data-contracts';
import * as signalR from "@microsoft/signalr";
import { GetPageantStore } from 'SessionState/Pageant/PageantStore';
import { GetPageantAOCStore } from 'SessionState/Pageant/PageantAOCStore';
import { GetPageantRegistrationStore } from 'SessionState/Pageant/PageantRegistrationStore';
import { GetEventStore } from 'SessionState/Event/EventStore';
import { GetEventJudgeStore } from 'SessionState/Judge/EventJudgeStore';
import { GetPageantAwardStore } from 'SessionState/Pageant/PageantAwardStore';
import {
  RegisteredContestant,
  PageantAvailableAOC,
  formatScore,
} from 'data/TabulationDataProvider';
import { getAgeGroupName } from 'helpers/PageantHelper';
import { localScoreRepository } from 'Repositories/Local/localScoreRepository';
import { judgeScoreSyncService } from 'Services/judgeScoreSyncService';
import { toast } from 'react-toastify';
import LoadingOverlay from 'views/components/LoadingOverlay';

type JudgeScoreMap = Record<number, ReceivedScoreBaseModel[]>;

type FetchJudgeProviderParams = {
  eventJudgeId: number;
  eventId: number;
};

type RefetchJudgeScoresFilters = {
  registeredPageantContestantId?: number;
  aocId?: number;
  aocScoringCategoryId?: number;
};

type RefetchJudgeContestantsParams = {
  registeredContestantIds?: number[];
};

type PageantAvailableAward = any;
type AwardRelationship = any;

type RefetchJudgeAwardsOptions = {
  registeredPageantContestantId?: number;
};

type RefetchAwardRelationshipParams = {
  registeredPageantContestantId: number;
  pageantAvailableTitleId: number;
};

export interface JudgeDataContextType {
  eventJudgeId: number;
  eventId: number;

  judge: EventJudgeModel | null;
  judgeId: number | null;

  pageant: PageantModel | null;
  eventDivisions: EventDivisionBaseModel[];
  ageGroupsOrdered: PageantAgeGroupBaseModel[];
  hasAgeGroups: boolean;

  pageantAvailableAOC: PageantAvailableAOC[];
  pageantAvailableAwards: PageantAvailableAward[];
  setPageantAvailableAwards: React.Dispatch<
    React.SetStateAction<PageantAvailableAward[]>
  >;

  awardRelationships: AwardRelationship[];
  setAwardRelationships: React.Dispatch<
    React.SetStateAction<AwardRelationship[]>
  >;

  refetchAwardRelationship: (
  params: RefetchAwardRelationshipParams,
) => Promise<AwardRelationship | null>;

updateAwardRelationshipLocal: (
  params: RefetchAwardRelationshipParams,
  relationship: AwardRelationship,
) => void;

  existingAOCScoringCategories: Record<
    number,
    (AOCScoringCategoryModel & { scoringCategoryName?: string })[]
  >;

  registeredContestants: RegisteredContestant[];
  setRegisteredContestants: React.Dispatch<
    React.SetStateAction<RegisteredContestant[]>
  >;

  allFetchedScores: JudgeScoreMap;
  setAllFetchedScores: React.Dispatch<React.SetStateAction<JudgeScoreMap>>;
  receivedScores: ReceivedScoreBaseModel[];
  setReceivedScores: React.Dispatch<
    React.SetStateAction<ReceivedScoreBaseModel[]>
  >;

  isLoading: boolean;
  isReady: boolean;
  loadingText?: string;

fetchInitialJudgeData: (options?: { force?: boolean; silent?: boolean }) => Promise<void>;

  refetchReceivedScores: (
    filters?: RefetchJudgeScoresFilters,
  ) => Promise<ReceivedScoreBaseModel[]>;

  refetchRegisteredContestants: (
    params?: RefetchJudgeContestantsParams,
    opts?: { replaceAll?: boolean },
  ) => Promise<RegisteredContestant[]>;

  refetchPageantAvailableAwards: (
    options?: RefetchJudgeAwardsOptions,
  ) => Promise<PageantAvailableAward[]>;

  refetchAwardRelationships: () => Promise<AwardRelationship[]>;


  saveScores: (rows: ReceivedScoreBaseModel[]) => Promise<void>;
 deleteScores: (scoreIds: number[]) => Promise<void>;

  isEnteredForAOC: (
    contestant: RegisteredContestant | null | undefined,
    aocId: number | null,
  ) => boolean;

  getContestantsForAgeGroup: (
    ageGroupName: string | null,
    aocId: number | null,
    divisionId: number | null,
    isEntered: boolean | null,
  ) => RegisteredContestant[];

    getContestantsForAgeGroupOrDivisions: (
    ageGroupName: string | null,
    aocId: number | null,
    divisionId: number | null,
    isEntered: boolean | null,
  ) => RegisteredContestant[];

  orderPageantAgeGroupsYoungestToOldest: (
    ageGroups: PageantAgeGroupBaseModel[],
  ) => PageantAgeGroupBaseModel[];

  calculateTotalPossiblePoints: (aocId: number) => number;

  calculateJudgeCategoryScore: (
    contestantId: number,
    aocScoreCategoryId: number,
  ) => number;

  calculateJudgeAOCTotalScore: (
    aocId: number,
    contestantId: number,
  ) => number;

  calculateJudgeAOCFinalScoreWithDrops: (
    aocId: number,
    contestantId: number,
  ) => { finalScore: number; droppedScores: number[] };

  getContestantAOCDetail: (
    contestant: RegisteredContestant | null | undefined,
    aocId: number | null,
  ) => any;

  formatScore: (score: number | null | undefined) => string;

  updateJudgeProfile: (payload: {
    judgeName: string;
    acceptedTerms: boolean;
  }) => Promise<EventJudgeModel | null>;
}

const defaultJudgeDataContext: JudgeDataContextType = {
  eventJudgeId: 0,
  eventId: 0,

  judge: null,
  judgeId: null,

  pageant: null,
  eventDivisions: [],
  ageGroupsOrdered: [],
  hasAgeGroups: false,

  pageantAvailableAOC: [],
  pageantAvailableAwards: [],
  setPageantAvailableAwards: () => {},

  awardRelationships: [],
  setAwardRelationships: () => {},

  refetchAwardRelationship: async () => null,
  updateAwardRelationshipLocal: () => {},

  existingAOCScoringCategories: {},

  registeredContestants: [],
  setRegisteredContestants: () => {},

  allFetchedScores: {},
  setAllFetchedScores: () => {},
  receivedScores: [],
  setReceivedScores: () => {},

  isLoading: false,
  isReady: false,
  loadingText: undefined,

  fetchInitialJudgeData: async () => {},
  refetchReceivedScores: async () => [],
  refetchRegisteredContestants: async () => [],
  refetchPageantAvailableAwards: async () => [],
  refetchAwardRelationships: async () => [],
  saveScores: async () => {},
  deleteScores: async () => {},
  isEnteredForAOC: () => false,
  getContestantsForAgeGroup: () => [],
  getContestantsForAgeGroupOrDivisions: () => [],

  orderPageantAgeGroupsYoungestToOldest: () => [],

  calculateTotalPossiblePoints: () => 0,
  calculateJudgeCategoryScore: () => 0,
  calculateJudgeAOCTotalScore: () => 0,
  calculateJudgeAOCFinalScoreWithDrops: () => ({
    finalScore: 0,
    droppedScores: [],
  }),

  getContestantAOCDetail: () => null,

  formatScore,

  updateJudgeProfile: async () => null,
};

export const JudgeDataContext =
  createContext<JudgeDataContextType>(defaultJudgeDataContext);

export const useJudgeData = (): JudgeDataContextType =>
  useContext(JudgeDataContext);

interface JudgeDataProviderProps extends FetchJudgeProviderParams {
  children: React.ReactNode;
}

type JudgeDataProviderCacheSnapshot = {
  judge: EventJudgeModel | null;
  judgeId: number | null;
  pageant: PageantModel | null;
  eventDivisions: EventDivisionBaseModel[];
  ageGroupsOrdered: PageantAgeGroupBaseModel[];
  pageantAvailableAOC: PageantAvailableAOC[];
  pageantAvailableAwards: PageantAvailableAward[];
  awardRelationships: AwardRelationship[];
  registeredContestants: RegisteredContestant[];
  allFetchedScores: JudgeScoreMap;
  receivedScores: ReceivedScoreBaseModel[];
  cachedAt: number;
};

const judgeDataProviderCache = new Map<string, JudgeDataProviderCacheSnapshot>();
const JUDGE_DATA_CACHE_TTL_MS = 1000 * 60 * 15;


const getJudgeDataCacheKey = (eventJudgeId: number, eventId: number): string =>
  `${eventJudgeId}:${eventId}`;

const getAwardKey = (award: any): string => {
  return String(
    award?.pageantAvailableAwardId ??
      award?.pageantAwardId ??
      award?.awardId ??
      award?.id ??
      award?.name ??
      award?.awardName ??
      '',
  );
};

const mergeAwards = (
  previousRows: PageantAvailableAward[],
  incomingRows: PageantAvailableAward[],
): PageantAvailableAward[] => {
  const next = [...(previousRows ?? [])];

  for (const incomingRow of incomingRows ?? []) {
    const incomingKey = getAwardKey(incomingRow);

    if (!incomingKey) {
      next.push(incomingRow);
      continue;
    }

    const existingIndex = next.findIndex(
      (existingRow) => getAwardKey(existingRow) === incomingKey,
    );

    if (existingIndex >= 0) {
      next[existingIndex] = {
        ...next[existingIndex],
        ...incomingRow,
      };
    } else {
      next.push(incomingRow);
    }
  }

  return next;
};

const deriveEventDivisionsFromContestants = (
  contestants: any[],
): EventDivisionBaseModel[] => {
  const map = new Map<number, EventDivisionBaseModel>();

  for (const contestant of contestants ?? []) {
    const divisionId = Number(
      contestant?.attributes?.eventDivisionId ??
        contestant?.contestantAttributes?.eventDivisionId ??
        0,
    );

    if (!divisionId || map.has(divisionId)) continue;

    const divisionName = String(
      contestant?.attributes?.eventDivisionName ??
        contestant?.attributes?.eventDivision ??
        contestant?.contestantAttributes?.eventDivisionName ??
        contestant?.contestantAttributes?.eventDivision ??
        'Division',
    );

    map.set(divisionId, {
      id: divisionId,
      name: divisionName,
      eventDivisionId: divisionId,
      divisionName,
    } as unknown as EventDivisionBaseModel);
  }

  return Array.from(map.values()).sort((a: any, b: any) =>
    String(a?.name ?? a?.divisionName ?? '').localeCompare(
      String(b?.name ?? b?.divisionName ?? ''),
    ),
  );
};

export const JudgeDataProvider: React.FC<JudgeDataProviderProps> = ({
  children,
  eventJudgeId,
  eventId,
}) => {
  const judgeStore = GetEventJudgeStore();
  const pageantStore = GetPageantStore();
  const aocStore = GetPageantAOCStore();
  const registrationStore = GetPageantRegistrationStore();
  const eventStore = GetEventStore();
  const awardStore = GetPageantAwardStore();

  const initialFetchInFlightRef = useRef(false);


  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string | undefined>(undefined);

  const [syncLoaderVisible, setSyncLoaderVisible] = useState(false);
const [syncLoaderText, setSyncLoaderText] = useState('Syncing scores...');

  const [judge, setJudge] = useState<EventJudgeModel | null>(null);
  const [judgeId, setJudgeId] = useState<number | null>(null);

  const hasShownSyncedToastRef = useRef(false);
  const hasShownOfflineAlertRef = useRef(false);
const hasShownRestoredAlertRef = useRef(false);
const hasShownSyncedAlertRef = useRef(false);


  const [offlineSyncMessage, setOfflineSyncMessage] = useState<string | null>(null);
const [offlineSyncVariant, setOfflineSyncVariant] = useState<
  'warning' | 'success' | 'info'
>('warning');

const isMountedRef = useRef(true);
const isSyncingRef = useRef(false);
const wasOfflineRef = useRef(!navigator.onLine);

  const [pageant, setPageant] = useState<PageantModel | null>(null);
  const [eventDivisions, setEventDivisions] = useState<
    EventDivisionBaseModel[]
  >([]);
  const [ageGroupsOrdered, setAgeGroupsOrdered] = useState<
    PageantAgeGroupBaseModel[]
  >([]);

  const hasAgeGroups = useMemo(() => {
  return (ageGroupsOrdered ?? []).length > 0;
}, [ageGroupsOrdered]);

  const [pageantAvailableAOC, setPageantAvailableAOC] = useState<
    PageantAvailableAOC[]
  >([]);
  const [registeredContestants, setRegisteredContestants] = useState<
    RegisteredContestant[]
  >([]);
  const [allFetchedScores, setAllFetchedScores] = useState<JudgeScoreMap>({});
  const [receivedScores, setReceivedScores] = useState<
    ReceivedScoreBaseModel[]
  >([]);
  const [pageantAvailableAwards, setPageantAvailableAwards] = useState<
    PageantAvailableAward[]
  >([]);
  const [awardRelationships, setAwardRelationships] = useState<
    AwardRelationship[]
  >([]);

  const cacheKey = useMemo(
    () => getJudgeDataCacheKey(eventJudgeId, eventId),
    [eventJudgeId, eventId],
  );

  const hydrateFromCache = useCallback(
    (snapshot: JudgeDataProviderCacheSnapshot) => {
      setJudge(snapshot.judge);
      setJudgeId(snapshot.judgeId);
      setPageant(snapshot.pageant);
      setEventDivisions(snapshot.eventDivisions ?? []);
      setAgeGroupsOrdered(snapshot.ageGroupsOrdered ?? []);
      setPageantAvailableAOC(snapshot.pageantAvailableAOC ?? []);
      setPageantAvailableAwards(snapshot.pageantAvailableAwards ?? []);
      setAwardRelationships(snapshot.awardRelationships ?? []);
      setRegisteredContestants(snapshot.registeredContestants ?? []);
      setAllFetchedScores(snapshot.allFetchedScores ?? {});
      setReceivedScores(snapshot.receivedScores ?? []);
    },
    [],
  );

  const showLoader = useCallback((text: string) => {
    setLoadingText(text);
    setIsLoading(true);
  }, []);

  const hideLoader = useCallback(() => {
    setLoadingText(undefined);
    setIsLoading(false);
  }, []);

  useEffect(() => {
  isMountedRef.current = true;

  return () => {
    isMountedRef.current = false;
  };
}, []);

const getReceivedScoreId = (score: any): number => {
  return Number(
    score?.id ??
      score?.Id ??
      score?.receivedScoreId ??
      score?.ReceivedScoreId ??
      score?.receivedScoresId ??
      score?.scoreId ??
      score?.ScoreId ??
      0,
  );
};

const normalizeScoreRow = (row: any): ReceivedScoreBaseModel => ({
  ...row,
  id: getReceivedScoreId(row),
  registeredPageantContestantId:
    row?.registeredPageantContestantId ?? row?.registeredContestantId,
  aocId: row?.aocId ?? row?.pageantAvailableEventId,
  aocScoreCategoryId: row?.aocScoreCategoryId ?? row?.aOCScoreCategoryId,
  judgeId: row?.judgeId ?? row?.eventJudgeId,
  score: row?.score ?? row?.Score,
  notes:
    row?.notes ??
    row?.Notes ??
    row?.scoreNotes ??
    row?.ScoreNotes ??
    '',
});

  const updatePendingSyncMessage = useCallback(async () => {
  const pendingScores = await localScoreRepository.getPendingScores();
  const pendingScoreCount = pendingScores.length;

  if (!navigator.onLine && pendingScoreCount > 0) {
    setOfflineSyncVariant('warning');
    setOfflineSyncMessage(
      'Little to No Internet, scores are still saving and will sync later.',
    );
    return;
  }

  if (navigator.onLine && pendingScoreCount > 0) {
    setOfflineSyncVariant('info');
    setOfflineSyncMessage(
      `Internet Restored, ${pendingScoreCount} scores syncing! You can continue to score like normal!`,
    );
    return;
  }

  if (navigator.onLine && pendingScoreCount === 0) {
    setOfflineSyncVariant('success');
    setOfflineSyncMessage('All scores are saved and synced.');

    window.setTimeout(() => {
        if (!isMountedRef.current) return;
      setOfflineSyncMessage(null);
    }, 3000);
  }
}, []);

  const existingAOCScoringCategories = useMemo(() => {
    const map: Record<
      number,
      (AOCScoringCategoryModel & { scoringCategoryName?: string })[]
    > = {};

    for (const aoc of pageantAvailableAOC ?? []) {
      const normalized = ((aoc as any)?.aocScoreCategories ?? []).map(
        (cat: any) => ({
          ...cat,
          scoringCategoryName:
            cat?.scoringCategoryName ?? cat?.scoringCategory?.name ?? '',
        }),
      );

      map[(aoc as any).id] = normalized;
    }

    return map;
  }, [pageantAvailableAOC]);

  const orderPageantAgeGroupsYoungestToOldest = useCallback(
    (ageGroups: PageantAgeGroupBaseModel[]): PageantAgeGroupBaseModel[] => {
      return [...(ageGroups ?? [])].sort((a: any, b: any) => {
        const aStart = Number(a?.startAge ?? a?.minAge ?? a?.ageStart ?? 0);
        const bStart = Number(b?.startAge ?? b?.minAge ?? b?.ageStart ?? 0);

        if (aStart !== bStart) return aStart - bStart;

        const aEnd = Number(a?.endAge ?? a?.maxAge ?? a?.ageEnd ?? 999);
        const bEnd = Number(b?.endAge ?? b?.maxAge ?? b?.ageEnd ?? 999);

        return aEnd - bEnd;
      });
    },
    [],
  );

  const getContestantAOCDetail = useCallback(
    (
      contestant: RegisteredContestant | null | undefined,
      aocId: number | null,
    ) => {
      if (!contestant || !aocId) return null;

      return (
        (contestant as any)?.contestantAreasOfCompetitionDetails ?? []
      ).find(
        (detail: any) =>
          String(detail?.aocId ?? detail?.pageantAvailableEventId) ===
          String(aocId),
      );
    },
    [],
  );

  const isEnteredForAOC = useCallback(
    (
      contestant: RegisteredContestant | null | undefined,
      aocId: number | null,
    ) => {
      const detail = getContestantAOCDetail(contestant, aocId);

      if (!detail) return false;

      if (typeof detail?.isEntered === 'boolean') {
        return detail.isEntered;
      }

      return true;
    },
    [getContestantAOCDetail],
  );

  const updateJudgeProfile = useCallback(
    async (payload: { judgeName: string; acceptedTerms: boolean }) => {
      const updatedJudge = await judgeStore.updateLoggedInJudgeProfile(
        eventJudgeId,
        payload,
      );

      if (updatedJudge) {
        setJudge(updatedJudge);
        judgeDataProviderCache.delete(cacheKey);
      }

      return updatedJudge;
    },
    [judgeStore, eventJudgeId, cacheKey],
  );

  const getContestantsForAgeGroup = useCallback(
    (
      ageGroupName: string | null,
      aocId: number | null,
      divisionId: number | null,
      isEntered: boolean | null = null,
    ): RegisteredContestant[] => {
      if (!ageGroupName || !divisionId) return [];

      const filteredByGroupAndDivision = registeredContestants.filter(
        (contestant) => {
          const contestantAgeGroupName = contestant.attributes?.ageGroup ?? '';

          const contestantDivisionId = Number(
            contestant.attributes?.eventDivisionId ?? 0,
          );

          return (
            contestantAgeGroupName === ageGroupName &&
            contestantDivisionId === Number(divisionId)
          );
        },
      );

      if (isEntered === null || aocId == null) {
        return filteredByGroupAndDivision;
      }

      return filteredByGroupAndDivision.filter((contestant) => {
        const details = contestant.contestantAreasOfCompetitionDetails ?? [];

        const aocDetail = details.find(
          (detail) => Number(detail?.aocId) === Number(aocId),
        );

        return Boolean(aocDetail?.isEntered) === isEntered;
      });
    },
    [registeredContestants],
  );

  const getContestantsForAgeGroupOrDivisions = useCallback(
  (
    ageGroupName: string | null,
    aocId: number | null,
    divisionId: number | null,
    isEntered: boolean | null = null,
  ): RegisteredContestant[] => {
    if (!divisionId) return [];

    const filteredByDivision = registeredContestants.filter((contestant) => {
      const contestantDivisionId = Number(
        contestant.attributes?.eventDivisionId ??
          contestant.attributes?.eventDivisionId ??
          0,
      );

      return contestantDivisionId === Number(divisionId);
    });

    const filteredByGroupAndDivision = hasAgeGroups
      ? filteredByDivision.filter((contestant) => {
          const contestantAgeGroupName =
            contestant.attributes?.ageGroup ??
            '';

          return contestantAgeGroupName === ageGroupName;
        })
      : filteredByDivision;

    if (isEntered === null || aocId == null) {
      return filteredByGroupAndDivision;
    }

    return filteredByGroupAndDivision.filter((contestant) => {
      const details = contestant.contestantAreasOfCompetitionDetails ?? [];

      const aocDetail = details.find(
        (detail: any) =>
          Number(detail?.aocId ?? detail?.pageantAvailableEventId) ===
          Number(aocId),
      );

      return Boolean(aocDetail?.isEntered ?? true) === isEntered;
    });
  },
  [registeredContestants, hasAgeGroups],
);

  

  const calculateTotalPossiblePoints = useCallback(
    (aocId: number): number => {
      return (
        existingAOCScoringCategories[aocId]?.reduce(
          (sum, category: any) => sum + Number(category?.maxScore ?? 0),
          0,
        ) ?? 0
      );
    },
    [existingAOCScoringCategories],
  );

  const calculateJudgeCategoryScore = useCallback(
    (contestantId: number, aocScoreCategoryId: number): number => {
      if (!judgeId) return 0;

      const row = (allFetchedScores[contestantId] ?? []).find(
        (score: any) =>
          Number(score?.judgeId ?? score?.eventJudgeId) === Number(judgeId) &&
          Number(score?.aocScoreCategoryId) === Number(aocScoreCategoryId),
      );

      return Number(row?.score ?? 0);
    },
    [allFetchedScores, judgeId],
  );

  const calculateJudgeAOCTotalScore = useCallback(
    (aocId: number, contestantId: number): number => {
      const categories = existingAOCScoringCategories[aocId] ?? [];

      return categories.reduce((sum, category: any) => {
        const categoryId = Number(category?.id ?? 0);
        return sum + calculateJudgeCategoryScore(contestantId, categoryId);
      }, 0);
    },
    [existingAOCScoringCategories, calculateJudgeCategoryScore],
  );

  const calculateJudgeAOCFinalScoreWithDrops = useCallback(
    (aocId: number, contestantId: number) => {
      const total = calculateJudgeAOCTotalScore(aocId, contestantId);

      return {
        finalScore: total,
        droppedScores: [],
      };
    },
    [calculateJudgeAOCTotalScore],
  );

  const mergeScoresIntoMap = useCallback((rows: ReceivedScoreBaseModel[]) => {
    const nextMap: JudgeScoreMap = {};

    for (const row of rows ?? []) {
      const contestantId = Number(
        (row as any)?.registeredPageantContestantId ??
          (row as any)?.registeredContestantId ??
          0,
      );

      if (!contestantId) continue;

      if (!nextMap[contestantId]) {
        nextMap[contestantId] = [];
      }

      nextMap[contestantId].push(row);
    }

    return nextMap;
  }, []);

  const mergeScoreRowsIntoState = useCallback(
    (rows: ReceivedScoreBaseModel[], filters?: RefetchJudgeScoresFilters) => {
      // const incomingRows = rows ?? [];
      const incomingRows = (rows ?? []).map(normalizeScoreRow);

      if (!filters?.registeredPageantContestantId) {
        setReceivedScores(incomingRows);
        setAllFetchedScores(mergeScoresIntoMap(incomingRows));
        return;
      }

      const contestantId = Number(filters.registeredPageantContestantId);
      const incomingKeys = new Set(
        incomingRows.map((incomingRow: any) =>
          [
            Number(incomingRow?.registeredPageantContestantId ?? contestantId),
            Number(incomingRow?.aocId ?? incomingRow?.pageantAvailableEventId ?? 0),
            Number(incomingRow?.aocScoreCategoryId ?? 0),
            Number(incomingRow?.judgeId ?? incomingRow?.eventJudgeId ?? 0),
          ].join('|'),
        ),
      );

      setReceivedScores((prev: any[]) => {
        const survivors = (prev ?? []).filter((existingRow: any) => {
          if (
            Number(
              existingRow?.registeredPageantContestantId ??
                existingRow?.registeredContestantId,
            ) !== contestantId
          ) {
            return true;
          }

          const existingKey = [
            Number(
              existingRow?.registeredPageantContestantId ??
                existingRow?.registeredContestantId ??
                contestantId,
            ),
            Number(existingRow?.aocId ?? existingRow?.pageantAvailableEventId ?? 0),
            Number(existingRow?.aocScoreCategoryId ?? 0),
            Number(existingRow?.judgeId ?? existingRow?.eventJudgeId ?? 0),
          ].join('|');

          return !incomingKeys.has(existingKey);
        });

        return [...survivors, ...incomingRows];
      });

      setAllFetchedScores((prev) => {
        const existingRows = [...(prev?.[contestantId] ?? [])];

        const survivors = existingRows.filter((existingRow: any) => {
          const existingKey = [
            contestantId,
            Number(existingRow?.aocId ?? existingRow?.pageantAvailableEventId ?? 0),
            Number(existingRow?.aocScoreCategoryId ?? 0),
            Number(existingRow?.judgeId ?? existingRow?.eventJudgeId ?? 0),
          ].join('|');

          return !incomingKeys.has(existingKey);
        });

        return {
          ...prev,
          [contestantId]: [...survivors, ...incomingRows],
        };
      });
    },
    [mergeScoresIntoMap],
  );

  const mapPayloadAocsToProvider = useCallback(
    (payload: any): PageantAvailableAOC[] => {
      return (payload?.areasOfCompetition ?? []).map((aoc: any) => ({
        id: aoc.pageantAvailableEventId,
        name: aoc.aocName,
        competeOrder: aoc.competeOrder,
        areaOfCompetitionTypeId: aoc.aocTypeId,
        areaOfCompetitionType: aoc.aocTypeName
          ? {
              id: aoc.aocTypeId,
              name: aoc.aocTypeName,
            }
          : null,
        aocScoreCategories: (aoc.scoringCategories ?? []).map((cat: any) => ({
          id: cat.aocScoreCategoryId,
          aocScoreCategoryId: cat.aocScoreCategoryId,
          scoringCategoryId: cat.scoringCategoryId,
          scoringCategoryName: cat.scoringCategoryName,
          minScore: cat.minScore,
          maxScore: cat.maxScore,
        })),
      })) as PageantAvailableAOC[];
    },
    [],
  );

  const mapPayloadContestantsToProvider = useCallback(
    (payload: any): RegisteredContestant[] => {
      const ageGroupLabelById = new Map<number, string>();

      for (const group of payload?.pageant?.ageGroups ?? []) {
        const fallbackName = getAgeGroupName({
          id: group.ageGroupId,
          ageGroupId: group.ageGroupId,
          ageGroupName: group.ageGroupName,
          name: group.ageGroupName,
          isSupreme: group.isSupreme,
          startAge: group.startAge,
          endAge: group.endAge,
        } as any);

        ageGroupLabelById.set(
          Number(group.ageGroupId),
          group.ageGroupName?.trim() || fallbackName,
        );
      }

      return (payload?.contestants ?? []).map((contestant: any) => {
        const ageGroupId = contestant?.attributes?.ageGroupId ?? null;
       const ageGroupName =
  contestant?.attributes?.ageGroupName?.trim() ||
  contestant?.attributes?.ageGroup?.trim() ||
  (ageGroupId != null
    ? ageGroupLabelById.get(Number(ageGroupId))
    : '') ||
  '';

        return {
          id: contestant.registeredContestantId,
          registeredContestantId: contestant.registeredContestantId,
          contestantNumber: contestant.contestantNumber ?? '',
          contestantName: contestant.contestantName ?? '',
          firstName: contestant.contestantName?.split(' ')?.[0] ?? '',
          lastName:
            contestant.contestantName?.split(' ')?.slice(1).join(' ') ?? '',
          fullName: contestant.contestantName ?? '',
          selectedPackageIds: [],
          selectedTitleIds: [],
          selectedAocIds: (contestant.areasOfCompetition ?? []).map(
            (aoc: any) => aoc.pageantAvailableEventId,
          ),
          contestantAttributes: contestant.attributes ?? {},
          attributes: {
            ...contestant.attributes,
            ageGroup: ageGroupName,
            ageGroupName,
            eventDivisionId: contestant?.attributes?.eventDivisionId ?? null,
            eventDivisionName:
              contestant?.attributes?.eventDivisionName ?? '',
            supremeGroupIds: contestant?.attributes?.supremeGroupIds ?? [],
            supremeGroupNames: contestant?.attributes?.supremeGroupNames ?? [],
          },
          contestantAreasOfCompetitionDetails: (
            contestant.areasOfCompetition ?? []
          ).map((aoc: any) => ({
            ...aoc,
            aocId: aoc.pageantAvailableEventId,
            isEntered: aoc.isEntered ?? true,
          })),
        } as unknown as RegisteredContestant;
      });
    },
    [],
  );

  const mapPayloadScoresToProvider = useCallback(
    (payload: any): ReceivedScoreBaseModel[] => {
      return (payload?.receivedScores ?? []).map((score: any) => ({
        id: getReceivedScoreId(score),
        registeredPageantContestantId: score.registeredContestantId,
        aocId: score.pageantAvailableEventId,
        aocScoreCategoryId: score.aocScoreCategoryId,
        judgeId: score.eventJudgeId,
        score: score.score,
        notes: score.notes,
      })) as ReceivedScoreBaseModel[];
    },
    [],
  );

  const resolveJudgeAndPageantOrg = useCallback(async () => {
    let payload: any = null;

    if ((judgeStore as any)?.getJudgeEventHome) {
      payload = await (judgeStore as any).getJudgeEventHome(
        eventJudgeId,
        eventId,
      );
    } else if ((judgeStore as any)?.getJudgeEventHomeData) {
      payload = await (judgeStore as any).getJudgeEventHomeData(
        eventJudgeId,
        eventId,
      );
    } else if ((judgeStore as any)?.getJudgePortalData) {
      payload = await (judgeStore as any).getJudgePortalData(
        eventJudgeId,
        eventId,
      );
    } else if ((judgeStore as any)?.getById) {
      payload = await (judgeStore as any).getById(eventJudgeId);
    } else if ((judgeStore as any)?.getEventJudgeById) {
      payload = await (judgeStore as any).getEventJudgeById(eventJudgeId);
    }

    const resolvedJudge =
      payload?.judge ?? payload?.eventJudge ?? judgeStore?.CurrentJudge ?? null;

    const resolvedJudgeId =
      payload?.judgeId ??
      payload?.judge?.eventJudgeId ??
      payload?.judge?.id ??
      payload?.eventJudge?.id ??
      resolvedJudge?.eventJudgeId ??
      resolvedJudge?.id ??
      null;

    let resolvedEvent: any = payload?.event ?? null;

    if (!resolvedEvent && (eventStore as any)?.getEvent && eventId) {
      try {
        resolvedEvent = await (eventStore as any).getEvent(eventId);
      } catch (error) {
        console.warn('[JudgeDataProvider] Unable to fetch event details', error);
      }
    }

    const resolvedPageantId =
      payload?.pageantId ??
      payload?.pageant?.pageantId ??
      payload?.pageant?.id ??
      payload?.event?.pageantId ??
      resolvedEvent?.pageantId ??
      resolvedJudge?.pageantId ??
      null;

    let resolvedPageant =
      payload?.pageant ?? pageantStore?.CurrentPageant ?? null;

    if (
      !resolvedPageant &&
      resolvedPageantId &&
      (pageantStore as any)?.getPageant
    ) {
      await (pageantStore as any).getPageant(resolvedPageantId);
      resolvedPageant = (pageantStore as any)?.CurrentPageant ?? null;
    }

    setJudge(resolvedJudge);
    setJudgeId(resolvedJudgeId ? Number(resolvedJudgeId) : null);
    setPageant(resolvedPageant);

const mappedPayloadContestants = payload?.contestants?.length
  ? mapPayloadContestantsToProvider(payload)
  : [];

const divisionsFromPayload =
  payload?.eventDivisions ??
  payload?.divisions ??
  payload?.event?.eventDivisions ??
  payload?.event?.divisions ??
  resolvedEvent?.eventDivisions ??
  resolvedEvent?.divisions ??
  resolvedPageant?.eventDivisions ??
  resolvedPageant?.pageantDivisions ??
  resolvedPageant?.event?.eventDivisions ??
  resolvedPageant?.event?.divisions ??
  [];

const divisions =
  divisionsFromPayload?.length > 0
    ? divisionsFromPayload
    : deriveEventDivisionsFromContestants(mappedPayloadContestants);

setEventDivisions(divisions);

    const rawAgeGroups =
      payload?.pageant?.ageGroups ?? resolvedPageant?.pageantAgeGroups ?? [];

    const orderedAgeGroups = orderPageantAgeGroupsYoungestToOldest(
      (rawAgeGroups ?? []).filter((x: any) => !x?.isSupreme),
    );

    setAgeGroupsOrdered(orderedAgeGroups);

    return {
      payload,
      judge: resolvedJudge,
      judgeId: resolvedJudgeId ? Number(resolvedJudgeId) : null,
      event: resolvedEvent,
      pageant: resolvedPageant,
      pageantId: resolvedPageantId ? Number(resolvedPageantId) : null,
    };
  }, [
    eventJudgeId,
    eventId,
    judgeStore,
    pageantStore,
    eventStore,
    orderPageantAgeGroupsYoungestToOldest,
  ]);

  const resolveJudgeAndPageant = useCallback(async () => {
  let payload: any = null;

  if ((judgeStore as any)?.getJudgeEventHome) {
    payload = await (judgeStore as any).getJudgeEventHome(eventJudgeId, eventId);
  } else if ((judgeStore as any)?.getJudgeEventHomeData) {
    payload = await (judgeStore as any).getJudgeEventHomeData(eventJudgeId, eventId);
  } else if ((judgeStore as any)?.getJudgePortalData) {
    payload = await (judgeStore as any).getJudgePortalData(eventJudgeId, eventId);
  } else if ((judgeStore as any)?.getById) {
    payload = await (judgeStore as any).getById(eventJudgeId);
  } else if ((judgeStore as any)?.getEventJudgeById) {
    payload = await (judgeStore as any).getEventJudgeById(eventJudgeId);
  }

  const resolvedJudge =
    payload?.judge ?? payload?.eventJudge ?? judgeStore?.CurrentJudge ?? null;

  const resolvedJudgeId =
    payload?.judgeId ??
    payload?.judge?.eventJudgeId ??
    payload?.judge?.id ??
    payload?.eventJudge?.id ??
    resolvedJudge?.eventJudgeId ??
    resolvedJudge?.id ??
    null;

  const resolvedStatus = String(
    resolvedJudge?.status ??
      resolvedJudge?.judgeStatus ??
      '',
  ).toLowerCase();

  const resolvedIsInactive =
    resolvedStatus === 'inactive' ||
    resolvedJudge?.isActive === false ||
    resolvedJudge?.active === false;

  if (
    Number(resolvedJudgeId) === Number(eventJudgeId) &&
    resolvedIsInactive
  ) {
    console.warn('[JudgeDataProvider] Judge fetched as inactive. Hard logging out.', {
      resolvedJudgeId,
      eventJudgeId,
      resolvedJudge,
    });

  await (judgeStore as any).logoutInactiveJudgeAndClearSyncedData?.();

    return {
      payload,
      judge: null,
      judgeId: null,
      event: payload?.event ?? null,
      pageant: payload?.pageant ?? null,
      pageantId: null,
    };
  }

  if (resolvedJudge) {
    judgeStore.setCurrentJudge?.({
      ...judgeStore.CurrentJudge,
      ...resolvedJudge,
    });
  }

  let resolvedEvent: any = payload?.event ?? null;

  if (!resolvedEvent && (eventStore as any)?.getEvent && eventId) {
    try {
      resolvedEvent = await (eventStore as any).getEvent(eventId);
    } catch (error) {
      console.warn('[JudgeDataProvider] Unable to fetch event details', error);
    }
  }

  const resolvedPageantId =
    payload?.pageantId ??
    payload?.pageant?.pageantId ??
    payload?.pageant?.id ??
    payload?.event?.pageantId ??
    resolvedEvent?.pageantId ??
    resolvedJudge?.pageantId ??
    null;

  let resolvedPageant =
    payload?.pageant ?? pageantStore?.CurrentPageant ?? null;

  if (
    !resolvedPageant &&
    resolvedPageantId &&
    (pageantStore as any)?.getPageant
  ) {
    await (pageantStore as any).getPageant(resolvedPageantId);
    resolvedPageant = (pageantStore as any)?.CurrentPageant ?? null;
  }

  setJudge(resolvedJudge);
  setJudgeId(resolvedJudgeId ? Number(resolvedJudgeId) : null);
  setPageant(resolvedPageant);

  const divisions =
    payload?.event?.divisions ??
    resolvedEvent?.eventDivisions ??
    resolvedPageant?.pageantDivisions ??
    resolvedPageant?.event?.divisions ??
    [];

  setEventDivisions(divisions);

  const rawAgeGroups =
    payload?.pageant?.ageGroups ?? resolvedPageant?.pageantAgeGroups ?? [];

  const orderedAgeGroups = orderPageantAgeGroupsYoungestToOldest(
    (rawAgeGroups ?? []).filter((x: any) => !x?.isSupreme),
  );

  setAgeGroupsOrdered(orderedAgeGroups);

  return {
    payload,
    judge: resolvedJudge,
    judgeId: resolvedJudgeId ? Number(resolvedJudgeId) : null,
    event: resolvedEvent,
    pageant: resolvedPageant,
    pageantId: resolvedPageantId ? Number(resolvedPageantId) : null,
  };
}, [
  eventJudgeId,
  eventId,
  judgeStore,
  pageantStore,
  eventStore,
  orderPageantAgeGroupsYoungestToOldest,
]);

  const fetchPageantAvailableAOC = useCallback(
    async (
      pageantId: number,
      payload?: any,
      eventJudge?: EventJudgeModel | null,
    ) => {
      if (payload?.areasOfCompetition?.length) {
        const mappedRows = mapPayloadAocsToProvider(payload);
        setPageantAvailableAOC(mappedRows);
        return mappedRows;
      }

      let rows: PageantAvailableAOC[] = [];

      if ((aocStore as any)?.getPageantAvailableAOC) {
        rows = await (aocStore as any).getPageantAvailableAOC({ pageantId });
      } else if ((aocStore as any)?.getPageantAOC) {
        rows = await (aocStore as any).getPageantAOC(pageantId);
      } else if ((aocStore as any)?.fetchPageantAvailableAOC) {
        rows = await (aocStore as any).fetchPageantAvailableAOC({ pageantId });
      }

      const allowedIds = new Set(
        (((eventJudge as any)?.areasOfCompetition ?? []) as any[]).map(
          (x: any) => Number(x?.pageantAvailableEventId),
        ),
      );

      const filteredRows =
        allowedIds.size > 0
          ? (rows ?? []).filter((aoc: any) => allowedIds.has(Number(aoc.id)))
          : rows ?? [];

      setPageantAvailableAOC(filteredRows);
      return filteredRows;
    },
    [aocStore, mapPayloadAocsToProvider],
  );

  const fetchRegisteredContestantsInternal = useCallback(
    async (
      pageantId: number,
      params?: RefetchJudgeContestantsParams,
      opts?: { replaceAll?: boolean },
      payload?: any,
    ) => {
      if (payload?.contestants?.length) {
        const mappedRows = mapPayloadContestantsToProvider(payload);

        let filteredRows = mappedRows;

        if (params?.registeredContestantIds?.length) {
          const idSet = new Set(params.registeredContestantIds.map(Number));
          filteredRows = mappedRows.filter((x: any) =>
            idSet.has(Number(x?.id)),
          );
        }

        if (
          opts?.replaceAll === false &&
          params?.registeredContestantIds?.length
        ) {
          setRegisteredContestants((prev) => {
            const replaceIds = new Set(
              params.registeredContestantIds?.map(Number),
            );
            const survivors = prev.filter(
              (contestant: any) => !replaceIds.has(Number(contestant?.id)),
            );
            return [...survivors, ...filteredRows];
          });
        } else {
          setRegisteredContestants(filteredRows);
        }

        return filteredRows;
      }

      let rows: RegisteredContestant[] = [];

      if ((registrationStore as any)?.fetchRegisteredContestantsDynamic) {
        rows = await (registrationStore as any).fetchRegisteredContestantsDynamic(
          {
            pageantId,
            registeredContestantIds: params?.registeredContestantIds,
          },
          opts,
        );
      } else if ((registrationStore as any)?.getRegisteredContestants) {
        rows = await (registrationStore as any).getRegisteredContestants({
          pageantId,
          registeredContestantIds: params?.registeredContestantIds,
        });
      } else if ((registrationStore as any)?.fetchRegisteredContestants) {
        rows = await (registrationStore as any).fetchRegisteredContestants({
          pageantId,
          registeredContestantIds: params?.registeredContestantIds,
        });
      }

      if (
        opts?.replaceAll === false &&
        params?.registeredContestantIds?.length
      ) {
        setRegisteredContestants((prev) => {
          const replaceIds = new Set(
            params.registeredContestantIds?.map(Number),
          );
          const survivors = prev.filter(
            (contestant: any) =>
              !replaceIds.has(Number((contestant as any)?.id)),
          );
          return [...survivors, ...(rows ?? [])];
        });
      } else {
        setRegisteredContestants(rows ?? []);
      }

      return rows ?? [];
    },
    [registrationStore, mapPayloadContestantsToProvider],
  );

  

  const fetchJudgeScoresInternal = useCallback(
    async (
      pageantId: number,
      filters?: RefetchJudgeScoresFilters,
      payload?: any,
      judgeIdOverride?: number | null,
    ) => {
      const resolvedJudgeId = Number(judgeIdOverride ?? judgeId ?? 0);

      if (payload?.receivedScores) {
        let rows = mapPayloadScoresToProvider(payload);

        if (filters?.registeredPageantContestantId) {
          rows = rows.filter(
            (x: any) =>
              Number((x as any)?.registeredPageantContestantId) ===
              Number(filters.registeredPageantContestantId),
          );
        }

        if (filters?.aocId) {
          rows = rows.filter(
            (x: any) => Number((x as any)?.aocId) === Number(filters.aocId),
          );
        }

        if (filters?.aocScoringCategoryId) {
          rows = rows.filter(
            (x: any) =>
              Number((x as any)?.aocScoreCategoryId) ===
              Number(filters.aocScoringCategoryId),
          );
        }

        mergeScoreRowsIntoState(rows, filters);
        return rows;
      }

      if (!resolvedJudgeId) return [];

      let rows: ReceivedScoreBaseModel[] = [];

      if ((registrationStore as any)?.getReceivedScores) {
        rows = await (registrationStore as any).getReceivedScores({
          pageantId,
          registeredPageantContestantId: filters?.registeredPageantContestantId,
          judgeId: resolvedJudgeId,
          aocId: filters?.aocId,
          aocScoringCategoryId: filters?.aocScoringCategoryId,
        });
      } else if ((registrationStore as any)?.refetchReceivedScores) {
        rows = await (registrationStore as any).refetchReceivedScores({
          pageantId,
          registeredPageantContestantId: filters?.registeredPageantContestantId,
          judgeId: resolvedJudgeId,
          aocId: filters?.aocId,
          aocScoringCategoryId: filters?.aocScoringCategoryId,
        });
      } else if ((registrationStore as any)?.fetchReceivedScores) {
        rows = await (registrationStore as any).fetchReceivedScores({
          pageantId,
          registeredPageantContestantId: filters?.registeredPageantContestantId,
          judgeId: resolvedJudgeId,
          aocId: filters?.aocId,
          aocScoringCategoryId: filters?.aocScoringCategoryId,
        });
      }

      mergeScoreRowsIntoState(rows ?? [], filters);
      return rows ?? [];
    },
    [
      judgeId,
      registrationStore,
      mapPayloadScoresToProvider,
      mergeScoreRowsIntoState,
    ],
  );

  const fetchPageantAvailableAwardsInternal = useCallback(
    async (
      pageantId: number,
      options?: RefetchJudgeAwardsOptions,
      payload?: any,
    ) => {
      let rows: PageantAvailableAward[] = [];

      const payloadAwards =
        payload?.sideAwards ??
        payload?.pageantAvailableAwards ??
        payload?.availableAwards ??
        payload?.awards ??
        [];

      if (Array.isArray(payloadAwards) && payloadAwards.length > 0) {
        rows = payloadAwards;
      } else if ((awardStore as any)?.getPageantAvailableAwards) {
        rows = await (awardStore as any).getPageantAvailableAwards(
          pageantId,
          options,
        );
      } else if ((awardStore as any)?.fetchPageantAvailableAwards) {
        rows = await (awardStore as any).fetchPageantAvailableAwards(
          pageantId,
          options,
        );
      } else if ((awardStore as any)?.getPageantAwards) {
        rows = await (awardStore as any).getPageantAwards(pageantId, options);
      } else if ((awardStore as any)?.fetchPageantAwards) {
        rows = await (awardStore as any).fetchPageantAwards(pageantId, options);
      }

      if (options?.registeredPageantContestantId) {
        setPageantAvailableAwards((prev) => mergeAwards(prev, rows ?? []));
      } else {
        setPageantAvailableAwards(rows ?? []);
      }

      return rows ?? [];
    },
    [awardStore],
  );

  

  const fetchAwardRelationshipsInternal = useCallback(
    async (pageantId: number, payload?: any) => {
      let rows: AwardRelationship[] = [];

      const payloadRelationships =
        payload?.awardRelationships ??
        payload?.pageantAwardRelationships ??
        payload?.relationships ??
        payload?.sideAwards?.flatMap(
          (award: any) => award?.awardRelationships ?? [],
        ) ??
        [];

      if (
        Array.isArray(payloadRelationships) &&
        payloadRelationships.length > 0
      ) {
        rows = payloadRelationships;
      } else if ((awardStore as any)?.getAwardRelationshipsByPageant) {
        rows = await (awardStore as any).getAwardRelationshipsByPageant(
          pageantId,
        );
      } else if ((awardStore as any)?.fetchAwardRelationshipsByPageant) {
        rows = await (awardStore as any).fetchAwardRelationshipsByPageant(
          pageantId,
        );
      } else if ((awardStore as any)?.getPageantAwardRelationships) {
        rows = await (awardStore as any).getPageantAwardRelationships(
          pageantId,
        );
      } else if ((awardStore as any)?.fetchPageantAwardRelationships) {
        rows = await (awardStore as any).fetchPageantAwardRelationships(
          pageantId,
        );
      }

      setAwardRelationships(rows ?? []);
      return rows ?? [];
    },
    [awardStore],
  );

  const patchAwardRelationshipState = useCallback(
  (
    params: RefetchAwardRelationshipParams,
    relationship: AwardRelationship,
  ) => {
    const normalizedRelationship = {
      ...relationship,
      registeredPageantContestantId:
        relationship?.registeredPageantContestantId ??
        params.registeredPageantContestantId,
      pageantAvailableTitleId:
        relationship?.pageantAvailableTitleId ??
        params.pageantAvailableTitleId,
    };

    console.log("[JudgeDataProvider] Patching award relationship state", {
      params,
      normalizedRelationship,
      isWon: normalizedRelationship?.isWon,
    });

    setAwardRelationships((prev) => {
      const existingIndex = (prev ?? []).findIndex(
        (rel: any) =>
          Number(rel?.registeredPageantContestantId) ===
            Number(params.registeredPageantContestantId) &&
          Number(rel?.pageantAvailableTitleId) ===
            Number(params.pageantAvailableTitleId),
      );

      if (existingIndex < 0) {
        return [...(prev ?? []), normalizedRelationship];
      }

      const next = [...(prev ?? [])];
      next[existingIndex] = {
        ...next[existingIndex],
        ...normalizedRelationship,
      };

      return next;
    });

    setPageantAvailableAwards((prev) =>
      (prev ?? []).map((award: any) => {
        const awardId = Number(
          award?.id ??
            award?.pageantAvailableTitleId ??
            award?.pageantAvailableAwardId ??
            0,
        );

        if (awardId !== Number(params.pageantAvailableTitleId)) {
          return award;
        }

        const existingRelationships = award?.awardRelationships ?? [];

        const existingIndex = existingRelationships.findIndex(
          (rel: any) =>
            Number(rel?.registeredPageantContestantId) ===
            Number(params.registeredPageantContestantId),
        );

        let nextRelationships: AwardRelationship[];

        if (existingIndex >= 0) {
          nextRelationships = existingRelationships.map((rel: any, index: number) =>
            index === existingIndex
              ? {
                  ...rel,
                  ...normalizedRelationship,
                }
              : rel,
          );
        } else {
          nextRelationships = [...existingRelationships, normalizedRelationship];
        }

        return {
          ...award,
          awardRelationships: nextRelationships,
        };
      }),
    );

    judgeDataProviderCache.delete(cacheKey);
  },
  [cacheKey],
);

const refetchAwardRelationship = useCallback(
  async (
    params: RefetchAwardRelationshipParams,
  ): Promise<AwardRelationship | null> => {
    const pageantId = Number(
      (pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0,
    );

    if (!pageantId) return null;

    console.log("[JudgeDataProvider] Refetching one award relationship", {
      pageantId,
      ...params,
    });

    let relationship: AwardRelationship | null = null;

    if ((awardStore as any)?.getAwardRelationship) {
      relationship = await (awardStore as any).getAwardRelationship({
        pageantId,
        registeredPageantContestantId: params.registeredPageantContestantId,
        pageantAvailableTitleId: params.pageantAvailableTitleId,
      });
    } else if ((awardStore as any)?.fetchAwardRelationship) {
      relationship = await (awardStore as any).fetchAwardRelationship({
        pageantId,
        registeredPageantContestantId: params.registeredPageantContestantId,
        pageantAvailableTitleId: params.pageantAvailableTitleId,
      });
    } else {
      console.warn(
        "[JudgeDataProvider] No targeted award relationship fetch method found. Falling back to full relationship refresh.",
      );

      const rows = await fetchAwardRelationshipsInternal(pageantId);

      relationship =
        (rows ?? []).find(
          (rel: any) =>
            Number(rel?.registeredPageantContestantId) ===
              Number(params.registeredPageantContestantId) &&
            Number(rel?.pageantAvailableTitleId) ===
              Number(params.pageantAvailableTitleId),
        ) ?? null;
    }

    console.log("[JudgeDataProvider] Refetched one award relationship response", {
      params,
      relationship,
      isWon: relationship?.isWon,
    });

    if (relationship) {
      patchAwardRelationshipState(params, relationship);
    }

    return relationship;
  },
  [
    pageant,
    awardStore,
    fetchAwardRelationshipsInternal,
    patchAwardRelationshipState,
  ],
);

const updateAwardRelationshipLocal = useCallback(
  (
    params: RefetchAwardRelationshipParams,
    relationship: AwardRelationship,
  ) => {
    patchAwardRelationshipState(params, relationship);
  },
  [patchAwardRelationshipState],
);

const fetchInitialJudgeDataOrg = useCallback(
  async (options?: { force?: boolean; silent?: boolean }) => {
    if (initialFetchInFlightRef.current) return;

    const cached = judgeDataProviderCache.get(cacheKey);
    const cacheIsFresh =
      cached && Date.now() - cached.cachedAt < JUDGE_DATA_CACHE_TTL_MS;

    if (!options?.force && cached && cacheIsFresh) {
      hydrateFromCache(cached);
      return;
    }

    try {
      initialFetchInFlightRef.current = true;

      if (!options?.silent) {
        showLoader('Loading judge portal...');
      }

      const resolved = await resolveJudgeAndPageant();
      const pageantId =
        resolved?.pageantId ??
        (resolved?.pageant as any)?.id ??
        (resolved?.pageant as any)?.pageantId ??
        0;

      if (!pageantId) {
        console.warn('[JudgeDataProvider] No pageantId could be resolved.');
        return;
      }

      await Promise.all([
        fetchPageantAvailableAOC(pageantId, resolved?.payload, resolved?.judge),
        fetchRegisteredContestantsInternal(pageantId, undefined, undefined, resolved?.payload),
        fetchPageantAvailableAwardsInternal(pageantId, undefined, resolved?.payload),
        fetchAwardRelationshipsInternal(pageantId, resolved?.payload),
      ]);

      if (resolved?.judgeId) {
        await fetchJudgeScoresInternal(
          pageantId,
          undefined,
          resolved?.payload,
          resolved.judgeId,
        );
      }
    } catch (error) {
      console.error('[JudgeDataProvider] Failed to load initial judge data', error);
    } finally {
      initialFetchInFlightRef.current = false;

      if (!options?.silent) {
        hideLoader();
      }
    }
  },
  [
    cacheKey,
    hydrateFromCache,
    showLoader,
    hideLoader,
    resolveJudgeAndPageant,
    fetchPageantAvailableAOC,
    fetchRegisteredContestantsInternal,
    fetchPageantAvailableAwardsInternal,
    fetchAwardRelationshipsInternal,
    fetchJudgeScoresInternal,
  ],
);




const fetchInitialJudgeData = useCallback(
  async (options?: { force?: boolean; silent?: boolean }) => {
    if (initialFetchInFlightRef.current) return;

    const cached = judgeDataProviderCache.get(cacheKey);

    const cacheIsFresh =
      cached &&
      Date.now() - cached.cachedAt < JUDGE_DATA_CACHE_TTL_MS;

    // FAST MEMORY CACHE
    if (!options?.force && cached && cacheIsFresh) {
      hydrateFromCache(cached);
      return;
    }

    // INDEXED DB SNAPSHOT
    const cachedSnapshot =
      await localScoreRepository.getJudgePortalSnapshot<JudgeDataProviderCacheSnapshot>(
        cacheKey,
      );

    // HYDRATE IMMEDIATELY FROM OFFLINE CACHE
    if (!options?.force && cachedSnapshot) {
      hydrateFromCache(cachedSnapshot);
    }

    // IF OFFLINE, STOP HERE AND USE SNAPSHOT
    if (!navigator.onLine && cachedSnapshot) {
      setOfflineSyncVariant('warning');

      setOfflineSyncMessage(
        'Offline mode: using saved judge portal data. Scores will sync when internet returns.',
      );

      return;
    }

    try {
      initialFetchInFlightRef.current = true;

      // ONLY SHOW LOADER IF WE DON'T ALREADY HAVE DATA
      if (!options?.silent && !cachedSnapshot) {
        showLoader('Loading judge portal...');
      }

      const resolved = await resolveJudgeAndPageant();

      const pageantId =
        resolved?.pageantId ??
        (resolved?.pageant as any)?.id ??
        (resolved?.pageant as any)?.pageantId ??
        0;

      if (!pageantId) {
        console.warn(
          '[JudgeDataProvider] No pageantId could be resolved.',
        );
        return;
      }

      const [
        aocs,
        contestants,
        awards,
        relationships,
      ] = await Promise.all([
        fetchPageantAvailableAOC(
          pageantId,
          resolved?.payload,
          resolved?.judge,
        ),

        fetchRegisteredContestantsInternal(
          pageantId,
          undefined,
          undefined,
          resolved?.payload,
        ),

        fetchPageantAvailableAwardsInternal(
          pageantId,
          undefined,
          resolved?.payload,
        ),

        fetchAwardRelationshipsInternal(
          pageantId,
          resolved?.payload,
        ),
      ]);

      const scores = resolved?.judgeId
        ? await fetchJudgeScoresInternal(
            pageantId,
            undefined,
            resolved?.payload,
            resolved.judgeId,
          )
        : [];

      const snapshot: JudgeDataProviderCacheSnapshot = {
        judge: resolved.judge,

        judgeId: resolved.judgeId,

        pageant: resolved.pageant,

   eventDivisions:
  (
    resolved?.payload?.eventDivisions ??
    resolved?.payload?.divisions ??
    resolved?.payload?.event?.eventDivisions ??
    resolved?.payload?.event?.divisions ??
    resolved?.event?.eventDivisions ??
    resolved?.event?.divisions ??
    resolved?.pageant?.eventDivisions ??
    resolved?.pageant?.pageantDivisions ??
    resolved?.pageant?.event?.eventDivisions ??
    resolved?.pageant?.event?.divisions ??
    []
  )?.length > 0
    ? (
        resolved?.payload?.eventDivisions ??
        resolved?.payload?.divisions ??
        resolved?.payload?.event?.eventDivisions ??
        resolved?.payload?.event?.divisions ??
        resolved?.event?.eventDivisions ??
        resolved?.event?.divisions ??
        resolved?.pageant?.eventDivisions ??
        resolved?.pageant?.pageantDivisions ??
        resolved?.pageant?.event?.eventDivisions ??
        resolved?.pageant?.event?.divisions ??
        []
      )
    : deriveEventDivisionsFromContestants(contestants ?? []),

        ageGroupsOrdered:
          orderPageantAgeGroupsYoungestToOldest(
            (
              resolved?.payload?.pageant?.ageGroups ??
              resolved?.pageant?.pageantAgeGroups ??
              []
            ).filter((x: any) => !x?.isSupreme),
          ),

        pageantAvailableAOC: aocs ?? [],

        pageantAvailableAwards: awards ?? [],

        awardRelationships: relationships ?? [],

        registeredContestants: contestants ?? [],

        receivedScores: scores ?? [],

        allFetchedScores: mergeScoresIntoMap(scores ?? []),

        cachedAt: Date.now(),
      };

      // MEMORY CACHE
      judgeDataProviderCache.set(cacheKey, snapshot);

      // PERSIST TO INDEXED DB
      await localScoreRepository.saveJudgePortalSnapshot(
        cacheKey,
        snapshot,
      );

      // ENSURE UI USES FRESH SNAPSHOT
      hydrateFromCache(snapshot);

      // CLEAR OFFLINE WARNING IF WE SUCCESSFULLY REFRESHED ONLINE
    setOfflineSyncVariant('success');
      setOfflineSyncMessage(null);
    } catch (error) {
      console.error(
        '[JudgeDataProvider] Failed to load initial judge data',
        error,
      );

      const fallbackSnapshot =
        await localScoreRepository.getJudgePortalSnapshot<JudgeDataProviderCacheSnapshot>(
          cacheKey,
        );

      if (fallbackSnapshot) {
        hydrateFromCache(fallbackSnapshot);

        setOfflineSyncVariant('warning');

        setOfflineSyncMessage(
          'Offline mode: using saved judge portal data. Scores will sync when internet returns.',
        );
      }
    } finally {
      initialFetchInFlightRef.current = false;

      if (!options?.silent) {
        hideLoader();
      }
    }
  },
  [
    cacheKey,
    hydrateFromCache,
    showLoader,
    hideLoader,
    resolveJudgeAndPageant,
    fetchPageantAvailableAOC,
    fetchRegisteredContestantsInternal,
    fetchPageantAvailableAwardsInternal,
    fetchAwardRelationshipsInternal,
    fetchJudgeScoresInternal,
    orderPageantAgeGroupsYoungestToOldest,
    mergeScoresIntoMap,
    setOfflineSyncVariant,
    setOfflineSyncMessage,
  ],
);

  const refetchReceivedScores = useCallback(
    async (filters?: RefetchJudgeScoresFilters) => {
      const pageantId = Number(
        (pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0,
      );
      if (!pageantId) return [];

      const rows = await fetchJudgeScoresInternal(pageantId, filters);
      judgeDataProviderCache.delete(cacheKey);
      return rows;
    },
    [pageant, cacheKey, fetchJudgeScoresInternal],
  );

  const refetchRegisteredContestants = useCallback(
    async (
      params?: RefetchJudgeContestantsParams,
      opts?: { replaceAll?: boolean },
    ) => {
      const pageantId = Number(
        (pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0,
      );
      if (!pageantId) return [];

      const rows = await fetchRegisteredContestantsInternal(
        pageantId,
        params,
        opts,
      );

      judgeDataProviderCache.delete(cacheKey);
      return rows;
    },
    [pageant, cacheKey, fetchRegisteredContestantsInternal],
  );

  const refetchPageantAvailableAwards = useCallback(
    async (options?: RefetchJudgeAwardsOptions) => {
      const pageantId = Number(
        (pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0,
      );
      if (!pageantId) return [];

      const rows = await fetchPageantAvailableAwardsInternal(pageantId, options);
      judgeDataProviderCache.delete(cacheKey);
      return rows;
    },
    [pageant, cacheKey, fetchPageantAvailableAwardsInternal],
  );

  const refetchAwardRelationships = useCallback(async () => {
    const pageantId = Number(
      (pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0,
    );
    if (!pageantId) return [];

    const rows = await fetchAwardRelationshipsInternal(pageantId);
    judgeDataProviderCache.delete(cacheKey);
    return rows;
  }, [pageant, cacheKey, fetchAwardRelationshipsInternal]);





  const saveScoresOrg = useCallback(
  async (rows: ReceivedScoreBaseModel[]) => {
    const pageantId = Number(
      (pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0,
    );

    if (!pageantId || !(rows?.length > 0)) return;

    // 1. Always save locally first
const initialSyncStatus = navigator.onLine ? 'synced' : 'pending';

await localScoreRepository.upsertScores({
  pageantId,
  eventId,
  eventJudgeId,
  rows,
  syncStatus: initialSyncStatus,
});

if (!navigator.onLine) {
  await updatePendingSyncMessage();
}

    // 2. Immediately update screen state
    mergeScoreRowsIntoState(rows, {
      registeredPageantContestantId: Number(
        (rows[0] as any)?.registeredPageantContestantId ?? 0,
      ),
    });

    // 3. Try real DB only if online
    if (navigator.onLine) {
      try {
        if ((registrationStore as any)?.saveScores) {
          await (registrationStore as any).saveScores(pageantId, rows);
        } else if ((registrationStore as any)?.saveJudgeScores) {
          await (registrationStore as any).saveJudgeScores(pageantId, rows);
        }

const recordIds = rows.map((row: any) =>
  [
    eventJudgeId,
    Number(
      row.registeredPageantContestantId ??
        row.registeredContestantId ??
        0,
    ),
    Number(
      row.aocScoreCategoryId ??
        row.AOCScoreCategoryId ??
        row.aOCScoreCategoryId ??
        row.aocScoringCategoryId ??
        0,
    ),
  ].join('|'),
);

await localScoreRepository.markScoresSynced(recordIds);
      } catch (error) {
        console.warn("[JudgeDataProvider] Saved locally, sync pending.", error);
      }
    }

    judgeDataProviderCache.delete(cacheKey);
  },
  [
    pageant,
    eventId,
    eventJudgeId,
    registrationStore,
    cacheKey,
    mergeScoreRowsIntoState,
    updatePendingSyncMessage,
  ],
);

const saveScores = useCallback(
  async (rows: ReceivedScoreBaseModel[]) => {
    const pageantId = Number(
      (pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0,
    );

    if (!pageantId || !(rows?.length > 0)) return;

    const registeredContestantId = Number(
      (rows[0] as any)?.registeredPageantContestantId ??
        (rows[0] as any)?.registeredContestantId ??
        0,
    );

    const initialSyncStatus = navigator.onLine ? 'synced' : 'pending';

    await localScoreRepository.upsertScores({
      pageantId,
      eventId,
      eventJudgeId,
      rows,
      syncStatus: initialSyncStatus,
    });

    if (!navigator.onLine) {
      await updatePendingSyncMessage();
    }

    // Immediate optimistic UI update
    mergeScoreRowsIntoState(rows, {
      registeredPageantContestantId: registeredContestantId,
    });

    if (navigator.onLine) {
      try {
        if ((registrationStore as any)?.saveScores) {
          await (registrationStore as any).saveScores(pageantId, rows);
        } else if ((registrationStore as any)?.saveJudgeScores) {
          await (registrationStore as any).saveJudgeScores(pageantId, rows);
        }

        // IMPORTANT: refetch from DB so saved rows get their real ReceivedScore.Id
        if (registeredContestantId) {
          const freshRows = await fetchJudgeScoresInternal(pageantId, {
  registeredPageantContestantId: registeredContestantId,
});

console.log("[SAVE] Fresh rows returned after save", freshRows);
        }

        console.log(
  "[SAVE] Refetched scores after save",
  allFetchedScores?.[registeredContestantId]
);

        const recordIds = rows.map((row: any) =>
          [
            eventJudgeId,
            Number(
              row.registeredPageantContestantId ??
                row.registeredContestantId ??
                0,
            ),
            Number(
              row.aocScoreCategoryId ??
                row.AOCScoreCategoryId ??
                row.aOCScoreCategoryId ??
                row.aocScoringCategoryId ??
                0,
            ),
          ].join('|'),
        );

        await localScoreRepository.markScoresSynced(recordIds);
      } catch (error) {
        console.warn('[JudgeDataProvider] Saved locally, sync pending.', error);
      }
    }

    judgeDataProviderCache.delete(cacheKey);
  },
  [
    pageant,
    eventId,
    eventJudgeId,
    registrationStore,
    cacheKey,
    mergeScoreRowsIntoState,
    updatePendingSyncMessage,
    fetchJudgeScoresInternal,
  ],
);

const deleteScores = useCallback(
  async (scoreIds: number[]) => {
    if (!scoreIds?.length) return;

    try {
      const scoreIdSet = new Set(scoreIds.map(Number));

      await Promise.all(
        scoreIds.map((id) =>
          (registrationStore as any).hardDeleteScore(id)
        )
      );

      setReceivedScores((prev) =>
        (prev ?? []).filter(
          (row: any) => !scoreIdSet.has(getReceivedScoreId(row))
        )
      );

      setAllFetchedScores((prev) => {
        const next: JudgeScoreMap = {};

        Object.entries(prev ?? {}).forEach(([contestantId, rows]) => {
          next[Number(contestantId)] = (rows ?? []).filter(
            (row: any) => !scoreIdSet.has(getReceivedScoreId(row))
          );
        });

        return next;
      });

      judgeDataProviderCache.delete(cacheKey);

      await localScoreRepository.saveJudgePortalSnapshot(cacheKey, {
        judge,
        judgeId,
        pageant,
        eventDivisions,
        ageGroupsOrdered,
        pageantAvailableAOC,
        pageantAvailableAwards,
        awardRelationships,
        registeredContestants,
       receivedScores: receivedScores.filter(
  (row: any) => !scoreIdSet.has(getReceivedScoreId(row))
),
        allFetchedScores: Object.fromEntries(
          Object.entries(allFetchedScores ?? {}).map(([contestantId, rows]) => [
            contestantId,
            (rows ?? []).filter(
           (row: any) => !scoreIdSet.has(getReceivedScoreId(row))
            ),
          ])
        ) as JudgeScoreMap,
        cachedAt: Date.now(),
      });
    } catch (error) {
      console.error("[JudgeDataProvider] Failed to delete scores", error);
      throw error;
    }
  },
  [
    registrationStore,
    cacheKey,
    judge,
    judgeId,
    pageant,
    eventDivisions,
    ageGroupsOrdered,
    pageantAvailableAOC,
    pageantAvailableAwards,
    awardRelationships,
    registeredContestants,
    receivedScores,
    allFetchedScores,
  ],
);


useEffect(() => {
  let syncIntervalId: number | undefined;

  const saveScoresToRemote = async (params: {
    pageantId: number;
    eventId: number;
    eventJudgeId: number;
    rows: ReceivedScoreBaseModel[];
  }) => {
    if ((registrationStore as any)?.saveScores) {
      await (registrationStore as any).saveScores(params.pageantId, params.rows);
      return;
    }

    if ((registrationStore as any)?.saveJudgeScores) {
      await (registrationStore as any).saveJudgeScores(params.pageantId, params.rows);
    }
  };



const syncPendingScores = async () => {
  if (isSyncingRef.current) return;

  console.log('[JudgeDataProvider] syncPendingScores called', {
  isSyncing: isSyncingRef.current,
  online: navigator.onLine,
});

  let pendingBefore = await localScoreRepository.getPendingScores();

  if (!navigator.onLine) {
    if (pendingBefore.length > 0) {
      setOfflineSyncVariant('warning');
      setOfflineSyncMessage(
        'Little to No Internet, scores are still saving and will sync later.',
      );
    }

    return;
  }

  if (pendingBefore.length === 0) return;

  isSyncingRef.current = true;

  try {
    setSyncLoaderVisible(true);

    while (navigator.onLine) {
      pendingBefore = await localScoreRepository.getPendingScores();

      if (pendingBefore.length === 0) break;

      const syncText = `Please wait while we sync ${pendingBefore.length} saved score${
        pendingBefore.length === 1 ? '' : 's'
      }...`;

      setSyncLoaderText(syncText);
      setOfflineSyncVariant('info');
      setOfflineSyncMessage(syncText);

      await judgeScoreSyncService.syncPendingScores({
        saveScoresToRemote,
      });

      const pendingAfter = await localScoreRepository.getPendingScores();

if (pendingAfter.length === pendingBefore.length) {
  console.warn(
    '[JudgeDataProvider] Pending score sync did not reduce pending count.',
    {
      pendingBefore: pendingBefore.length,
      pendingAfter: pendingAfter.length,
    },
  );

  setOfflineSyncVariant('warning');
  setOfflineSyncMessage(
    `${pendingAfter.length} score${
      pendingAfter.length === 1 ? '' : 's'
    } still need to sync. Please check your connection.`,
  );

  setSyncLoaderVisible(false);
  break;
}

      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }

    const finalPending = await localScoreRepository.getPendingScores();

    if (finalPending.length === 0) {
      setOfflineSyncVariant('success');
      setOfflineSyncMessage('All scores are 100% synced.');

      window.setTimeout(() => {
        if (!isMountedRef.current) return;
        setOfflineSyncMessage(null);
      }, 3500);
    } else {
      setOfflineSyncVariant('warning');
      setOfflineSyncMessage(
        `${finalPending.length} score${finalPending.length === 1 ? '' : 's'} still need to sync.`,
      );
    }
  } finally {
    const stillPending = await localScoreRepository.getPendingScores();

  if (stillPending.length === 0) {
    setSyncLoaderVisible(false);
  }

  isSyncingRef.current = false;
  }
};



const startSyncLoop = () => {
  void syncPendingScores();

  if (syncIntervalId) {
    window.clearInterval(syncIntervalId);
  }

syncIntervalId = window.setInterval(async () => {
  if (!navigator.onLine) {
    wasOfflineRef.current = true;
    return;
  }

  const pending = await localScoreRepository.getPendingScores();

  if (pending.length > 0) {
    void syncPendingScores();
  }
}, 3000);
};

 const handleOffline = () => {
  wasOfflineRef.current = true;

  if (!hasShownOfflineAlertRef.current) {
    hasShownOfflineAlertRef.current = true;
    hasShownRestoredAlertRef.current = false;
    hasShownSyncedAlertRef.current = false;

    setOfflineSyncVariant('warning');
    setOfflineSyncMessage(
      'Little to No Internet, scores are still saving and will sync later.',
    );
  }
};

const handleOnline = () => {
  console.log('[JudgeDataProvider] Browser online event detected.');

  wasOfflineRef.current = false;
  hasShownOfflineAlertRef.current = false;
  hasShownRestoredAlertRef.current = false;
  hasShownSyncedAlertRef.current = false;

  isSyncingRef.current = false;

  window.setTimeout(() => {
    void syncPendingScores();
  }, 250);

  startSyncLoop();
};

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void syncPendingScores();
    }
  };

  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);
  window.addEventListener('focus', syncPendingScores);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  startSyncLoop();

  return () => {
    if (syncIntervalId) {
      window.clearInterval(syncIntervalId);
    }

    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('focus', syncPendingScores);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [
  registrationStore,
  eventId,
  eventJudgeId,
]);

useEffect(() => {
  if (!eventJudgeId || !eventId || !pageant || !judgeId) return;

  judgeDataProviderCache.set(cacheKey, {
    judge,
    judgeId,
    pageant,
    eventDivisions,
    ageGroupsOrdered,
    pageantAvailableAOC,
    pageantAvailableAwards,
    awardRelationships,
    registeredContestants,
    allFetchedScores,
    receivedScores,
    cachedAt: Date.now(),
  });
}, [
  cacheKey,
  eventJudgeId,
  eventId,
  judge,
  judgeId,
  pageant,
  eventDivisions,
  ageGroupsOrdered,
  pageantAvailableAOC,
  pageantAvailableAwards,
  awardRelationships,
  registeredContestants,
  allFetchedScores,
  receivedScores,
]);

useEffect(() => {
  if (!eventJudgeId || !eventId) return;

  void fetchInitialJudgeData();
}, [eventJudgeId, eventId, fetchInitialJudgeData]);

// useEffect(() => {
//   if (!eventJudgeId || !eventId) return;

//   let pollInterval: number | undefined;
//   let isChecking = false;

// const hardLogoutInactiveJudge = async (source: string, payload?: any) => {
//   console.warn(
//     `[JudgeDataProvider] Judge inactive from ${source}. Hard logging out.`,
//     payload,
//   );

//   toast.error('Your judge access has been marked inactive. Logging you out.', {
//     toastId: 'judge-inactive-hard-logout',
//   });

//   await (judgeStore as any).logoutInactiveJudgeAndClearSyncedData?.();

//   window.location.replace('/judge/login');
// };



//   const checkJudgeStatus = async () => {
//     if (isChecking || !navigator.onLine) return;

//     isChecking = true;

//     try {
//       const latestPayload = await (judgeStore as any).getJudgeEventHome?.(
//         Number(eventJudgeId),
//         Number(eventId),
//       );

//       const latestJudge =
//         latestPayload?.judge ??
//         latestPayload?.eventJudge ??
//         latestPayload ??
//         null;

//       const latestJudgeId = Number(
//         latestJudge?.id ??
//           latestJudge?.eventJudgeId ??
//           latestPayload?.judgeId ??
//           0,
//       );

//       const latestStatus = String(
//         latestJudge?.status ??
//           latestJudge?.judgeStatus ??
//           '',
//       ).toLowerCase();

//       const isInactive =
//         latestStatus === 'inactive' ||
//         latestJudge?.isActive === false ||
//         latestJudge?.active === false;

//       console.log('[JudgeDataProvider] Polled judge status', {
//         latestJudgeId,
//         eventJudgeId,
//         latestStatus,
//         isActive: latestJudge?.isActive,
//         active: latestJudge?.active,
//         isInactive,
//       });

//       if (
//         latestJudgeId === Number(eventJudgeId) &&
//         isInactive
//       ) {
//         await hardLogoutInactiveJudge('polling', latestJudge);
//       }
//     } catch (error) {
//       console.warn('[JudgeDataProvider] Failed polling judge status', error);
//     } finally {
//       isChecking = false;
//     }
//   };

//   void checkJudgeStatus();

//   pollInterval = window.setInterval(() => {
//     void checkJudgeStatus();
//   }, 5000);

//   return () => {
//     if (pollInterval) {
//       window.clearInterval(pollInterval);
//     }
//   };
// }, [eventJudgeId, eventId, judgeStore]);


useEffect(() => {
  if (!eventJudgeId || !eventId) return;

  let pollInterval: number | undefined;
  let isChecking = false;

  const hardLogoutInactiveJudge = async (source: string, payload?: any) => {
    console.warn(
      `[JudgeDataProvider] Judge inactive from ${source}. Hard logging out.`,
      payload,
    );

    toast.error('Your judge access has been marked inactive. Logging you out.', {
      toastId: 'judge-inactive-hard-logout',
    });

    await (judgeStore as any).logoutInactiveJudgeAndClearSyncedData?.();

    window.location.replace('/judge/login');
  };

  const checkJudgeStatus = async (source: string) => {
    if (isChecking || !navigator.onLine) return;

    isChecking = true;

    try {
      const latestPayload = await (judgeStore as any).getJudgeEventHome?.(
        Number(eventJudgeId),
        Number(eventId),
      );

      const latestJudge =
        latestPayload?.judge ??
        latestPayload?.eventJudge ??
        latestPayload ??
        null;

      const latestJudgeId = Number(
        latestJudge?.id ??
          latestJudge?.eventJudgeId ??
          latestPayload?.judgeId ??
          0,
      );

      const latestStatus = String(
        latestJudge?.status ?? latestJudge?.judgeStatus ?? '',
      ).toLowerCase();

      const isInactive =
        latestStatus === 'inactive' ||
        latestJudge?.isActive === false ||
        latestJudge?.active === false;

      if (latestJudgeId === Number(eventJudgeId) && isInactive) {
        await hardLogoutInactiveJudge(source, latestJudge);
      }
    } catch (error) {
      console.warn('[JudgeDataProvider] Failed checking judge status', error);
    } finally {
      isChecking = false;
    }
  };

  const startStatusCheck = () => {
    void checkJudgeStatus('initial/focus check');

    if (pollInterval) {
      window.clearInterval(pollInterval);
    }

    pollInterval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void checkJudgeStatus('slow polling');
    }, 30000);
  };

  const handleFocus = () => {
    void checkJudgeStatus('window focus');
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void checkJudgeStatus('tab visible');
    }
  };

  startStatusCheck();

  window.addEventListener('focus', handleFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    if (pollInterval) {
      window.clearInterval(pollInterval);
    }

    window.removeEventListener('focus', handleFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [eventJudgeId, eventId, judgeStore]);




const fetchInitialJudgeDataRef = useRef(fetchInitialJudgeData);

useEffect(() => {
  fetchInitialJudgeDataRef.current = fetchInitialJudgeData;
}, [fetchInitialJudgeData]);

useEffect(() => {
  if (!eventId || !eventJudgeId) return;

  let refreshTimeoutId: number | undefined;

  const refreshJudgePortal = (eventName?: string, payload?: any) => {
    console.log(`[JudgeDataProvider][SignalR] Update received: ${eventName}`, payload);

    const eventJudgeIds = [
  payload?.judgeId,
  payload?.eventJudgeId,
  payload?.deletedJudgeId,
  payload?.affectedJudgeId,
  ...(payload?.judgeIds ?? []),
  ...(payload?.eventJudgeIds ?? []),
]
  .map(Number)
  .filter((id) => Number.isFinite(id) && id > 0);

const isScoreEvent =
  eventName === 'ScoresUpdated' ||
  eventName === 'ScoreDeleted' ||
  eventName === 'ScoresDeleted';

if (isScoreEvent) {
  if (eventJudgeIds.length === 0) {
    console.warn('[JudgeDataProvider][SignalR] Score event missing judge id. Ignoring.', payload);
    return;
  }

  if (!eventJudgeIds.includes(Number(eventJudgeId))) {
    return;
  }
}

    if (refreshTimeoutId) {
      window.clearTimeout(refreshTimeoutId);
    }

    refreshTimeoutId = window.setTimeout(() => {
      console.log('[JudgeDataProvider][SignalR] Refetching judge portal data...');

      judgeDataProviderCache.delete(cacheKey);

      if (
  eventName === 'ContestantUpdated' ||
  eventName === 'ContestantAdded' ||
  eventName === 'ContestantDeleted' ||
  eventName === 'ContestantAocUpdated' ||
  payload?.reason === 'ContestantAocUpdated'
) {
  toast.info('Contestant updates have been applied.');
} else if (
  eventName === 'ScoresUpdated' ||
  eventName === 'ScoreDeleted' ||
  eventName === 'ScoresDeleted'
) {
  toast.info('Scores have been updated.');
}

    void fetchInitialJudgeDataRef.current?.({ force: true });
    }, 300);
  };

  const connection = new signalR.HubConnectionBuilder()
    .withUrl('/hubs/event-live')
    .withAutomaticReconnect()
    .build();

  connection.onreconnecting((error) => {
    console.warn('[JudgeDataProvider][SignalR] Reconnecting...', error);
  });

connection.onreconnected((connectionId) => {
  console.log('[JudgeDataProvider][SignalR] Reconnected:', connectionId);

  if (toast.isActive('judge-live-updates-reconnected')) return;

  toast.success('Live updates reconnected.', {
    toastId: 'judge-live-updates-reconnected',
  });
});

  connection.onclose((error) => {
    console.warn('[JudgeDataProvider][SignalR] Connection closed:', error);
  });

  connection.on('JudgePortalUpdated', (payload) => {
    refreshJudgePortal('JudgePortalUpdated', payload);
  });

  connection.on('ContestantUpdated', (payload) => {
    refreshJudgePortal('ContestantUpdated', payload);
  });

  connection.on('ContestantAdded', (payload) => {
    refreshJudgePortal('ContestantAdded', payload);
  });

  connection.on('ContestantDeleted', (payload) => {
    refreshJudgePortal('ContestantDeleted', payload);
  });

  connection.on('ContestantAocUpdated', (payload) => {
    refreshJudgePortal('ContestantAocUpdated', payload);
  });

  connection.on('ScoresUpdated', (payload) => {
    refreshJudgePortal('ScoresUpdated', payload);
  });

connection.on('ScoreDeleted', (payload) => {
  refreshJudgePortal('ScoreDeleted', payload);
});

connection.on('ScoresDeleted', (payload) => {
  refreshJudgePortal('ScoresDeleted', payload);
});

  connection.on('AwardsUpdated', (payload) => {
    refreshJudgePortal('AwardsUpdated', payload);
  });

connection.on('JudgeStatusChanged', (payload) => {
  console.log(
    '[JudgeDataProvider][SignalR] JudgeStatusChanged',
    payload,
  );

  const changedJudgeId = Number(payload?.judgeId ?? 0);

  const liveStatus = String(
    payload?.liveStatus ?? '',
  ).toLowerCase();

  const judgeStatus = String(
    payload?.status ??
      payload?.judgeStatus ??
      '',
  ).toLowerCase();

  const isInactive = judgeStatus === 'inactive' ||
    payload?.isActive === false;

  if (
    changedJudgeId === Number(eventJudgeId) &&
    isInactive
  ) {
  console.log('[JudgeDataProvider][SignalR] HARD LOGOUT TRIGGERED', {
  changedJudgeId,
  eventJudgeId,
  payload,
});

    toast.error(
      'Your judge access has been marked inactive. Logging you out.',
    );

  window.setTimeout(() => {
  void (async () => {
    await (judgeStore as any).logoutJudgeHard?.();
  })();
}, 1200);
  }
});

let cancelled = false;

  void connection
    .start()
    .then(async () => {
          if (cancelled || !isMountedRef.current) return;
          
      console.log('[JudgeDataProvider][SignalR] Connected successfully.');

      await connection.invoke('JoinEventGroup', eventId);

      console.log(`[JudgeDataProvider][SignalR] Joined event group: event-${eventId}`);
    })
    .catch((error) => {
        if (cancelled || !isMountedRef.current) return;

      console.error('[JudgeDataProvider][SignalR] Connection failed:', error);
    });

return () => {
  cancelled = true;

  console.log('[JudgeDataProvider][SignalR] Cleaning up connection.');

  if (refreshTimeoutId) {
    window.clearTimeout(refreshTimeoutId);
  }

  connection.off('JudgePortalUpdated');
  connection.off('ContestantUpdated');
  connection.off('ContestantAdded');
  connection.off('ContestantDeleted');
  connection.off('ContestantAocUpdated');
  connection.off('ScoresUpdated');
  connection.off('AwardsUpdated');
  connection.off('JudgeStatusChanged');
  connection.off('ScoreDeleted');
connection.off('ScoresDeleted');

  void connection.stop();
};
}, [eventId, eventJudgeId, cacheKey, judgeStore]);



  const isReady = useMemo(() => {
    return !!pageant && !!judgeId;
  }, [pageant, judgeId]);

  const contextValue = useMemo<JudgeDataContextType>(
    () => ({
      eventJudgeId,
      eventId,

      judge,
      judgeId,

      pageant,
      eventDivisions,
      ageGroupsOrdered,
      hasAgeGroups,

      pageantAvailableAOC,
      pageantAvailableAwards,
      setPageantAvailableAwards,

      awardRelationships,
      setAwardRelationships,

      refetchAwardRelationship,
      updateAwardRelationshipLocal,

      existingAOCScoringCategories,

      registeredContestants,
      setRegisteredContestants,

      allFetchedScores,
      setAllFetchedScores,
      receivedScores,
      setReceivedScores,

      isLoading,
      isReady,
      loadingText,

      fetchInitialJudgeData,
      refetchReceivedScores,
      refetchRegisteredContestants,
      refetchPageantAvailableAwards,
      refetchAwardRelationships,
      saveScores,
      deleteScores,

      isEnteredForAOC,
      getContestantsForAgeGroup,
getContestantsForAgeGroupOrDivisions,

      orderPageantAgeGroupsYoungestToOldest,

      calculateTotalPossiblePoints,
      calculateJudgeCategoryScore,
      calculateJudgeAOCTotalScore,
      calculateJudgeAOCFinalScoreWithDrops,

      getContestantAOCDetail,

      formatScore,
      updateJudgeProfile,
    }),
    [
      eventJudgeId,
      eventId,
      judge,
      judgeId,
      pageant,
      eventDivisions,
      ageGroupsOrdered,
      hasAgeGroups,
      pageantAvailableAOC,
      pageantAvailableAwards,
      awardRelationships,
      refetchAwardRelationship,
      updateAwardRelationshipLocal,
      existingAOCScoringCategories,
      registeredContestants,
      allFetchedScores,
      receivedScores,
      isLoading,
      isReady,
      loadingText,
      fetchInitialJudgeData,
      refetchReceivedScores,
      refetchRegisteredContestants,
      refetchPageantAvailableAwards,
      refetchAwardRelationships,
      saveScores,
      deleteScores,
      isEnteredForAOC,
      getContestantsForAgeGroup,
      getContestantsForAgeGroupOrDivisions,
      orderPageantAgeGroupsYoungestToOldest,
      calculateTotalPossiblePoints,
      calculateJudgeCategoryScore,
      calculateJudgeAOCTotalScore,
      calculateJudgeAOCFinalScoreWithDrops,
      getContestantAOCDetail,
      updateJudgeProfile,
    ],
  );

  return (
    <JudgeDataContext.Provider value={contextValue}>
      {children}
       <LoadingOverlay
            show={syncLoaderVisible}
            text={syncLoaderText}
          />
    </JudgeDataContext.Provider>
  );
};

export default JudgeDataProvider;